import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  clearCompareQueueItems,
  loadCompareQueue,
  removeCompareQueueItem,
  saveCompareQueue,
  toggleCompareQueueItem,
  isInCompareQueue,
  COMPARE_QUEUE_STORAGE_KEY,
} from "Explore/Utils/compareQueueStorage";

const CompareSelectionContext = createContext(null);

export const CompareSelectionProvider = ({ children }) => {
  const [items, setItems] = useState(() => loadCompareQueue());

  const syncFromStorage = useCallback(() => {
    setItems(loadCompareQueue());
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === COMPARE_QUEUE_STORAGE_KEY) syncFromStorage();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [syncFromStorage]);

  const toggle = useCallback(({ compareType, id, label }) => {
    setItems((prev) => {
      const { items: next, action } = toggleCompareQueueItem(prev, {
        compareType,
        id,
        label,
      });
      if (action === "full") return prev;
      saveCompareQueue(next);
      return next;
    });
  }, []);

  const remove = useCallback((compareType, id) => {
    setItems((prev) => {
      const next = removeCompareQueueItem(prev, compareType, id);
      saveCompareQueue(next);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    clearCompareQueueItems();
    setItems([]);
  }, []);

  const isSelected = useCallback(
    (compareType, id) => isInCompareQueue(items, compareType, id),
    [items]
  );

  const value = useMemo(
    () => ({
      items,
      count: items.length,
      toggle,
      remove,
      clearAll,
      isSelected,
      refresh: syncFromStorage,
    }),
    [items, toggle, remove, clearAll, isSelected, syncFromStorage]
  );

  return (
    <CompareSelectionContext.Provider value={value}>
      {children}
    </CompareSelectionContext.Provider>
  );
};

export const useCompareSelection = () => {
  const ctx = useContext(CompareSelectionContext);
  if (!ctx) {
    throw new Error("useCompareSelection must be used within CompareSelectionProvider");
  }
  return ctx;
};
